import { useEffect, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import {
  OppositionWorkflowStep,
  getOppositionInformClientPreview,
  getOppositionProgress,
  sendOppositionInformClient,
} from "api/opposition";

export const OPPOSITION_INFORM_CLIENT_FORM_ID = "opposition-inform-client-form";

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function OppositionInformClientView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [hasSent, setHasSent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setHasSent(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getOppositionProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === OppositionWorkflowStep.OP_S1_05_InformClient
        );
        if (!cancelled && step) setHasSent(step.isCompleted);
      } catch {
        if (!cancelled) setHasSent(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getOppositionInformClientPreview(leadId)
      .then((res) => {
        if (cancelled) return;
        setPreviewMessage(res.message ?? null);
        setPhoneNumber(res.recipientPhone ?? "");
      })
      .catch((err) => {
        if (cancelled) return;
        setPreviewError(
          err instanceof Error ? err.message : "Failed to load preview"
        );
        setPreviewMessage(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const canSend = !previewError && !!previewMessage;

  const triggerSend = (mode: "send" | "resend") => {
    if (!leadId || sending) return;
    if (!canSend) {
      toast.error(
        previewError ?? "Complete the previous steps to generate the message."
      );
      return;
    }
    setConfirmMode(mode);
    setShowConfirmModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSent) {
      onSuccess?.(leadId);
      return;
    }
    triggerSend("send");
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    (async () => {
      try {
        await sendOppositionInformClient(leadId);
        setHasSent(true);
        toast.success(
          confirmMode === "resend"
            ? "Message resent to client successfully."
            : "Message sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_INFORM_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">Message Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px]">
          {previewLoading && !previewMessage ? (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Loading preview...
            </p>
          ) : previewMessage ? (
            <pre
              className="font-sans text-[12px] leading-5 text-[#222] m-0 whitespace-pre-wrap break-words"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {renderWhatsAppFormatting(previewMessage)}
            </pre>
          ) : (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {previewError ??
                "Complete the previous steps to generate the message."}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSent && (
          <button
            type="button"
            disabled={sending || !canSend}
            onClick={() => triggerSend("resend")}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewMessage ?? "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
